// Right-side toolbar above the grid: edit/lock toggle, hidden widget
// re-add dropdown, reset layout, share. Edit/share-import logic is in
// the parent (CockpitGrid + the page's CockpitShareLoader); this is
// the pure-UI surface.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiConfig';

function ShareButton({ scope, activeTab }) {
  const [status, setStatus] = useState('idle');
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleShare = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE_URL}/api/cockpit/presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope,
          name: activeTab.name,
          layout: activeTab.layout,
          hidden: activeTab.hidden,
          // Every per-instance picker / threshold / sort / interval lives
          // here (one entry per widget instance keyed by layout `i`). Without
          // shipping it, a shared link round-trips the geometry but resets
          // every widget back to its built-in defaults.
          widgetSettings: activeTab.widgetSettings || {},
        }),
      });
      if (!res.ok) throw new Error('share failed');
      const { shortId } = await res.json();
      const url = `${window.location.origin}${window.location.pathname}?preset=${shortId}`;
      await navigator.clipboard.writeText(url);
      setStatus('copied');
    } catch {
      setStatus('error');
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStatus('idle'), 2500);
  }, [scope, activeTab]);

  return (
    <button
      type="button"
      className={`cockpit-tb-btn cockpit-tb-share status-${status}`}
      onClick={handleShare}
      title="Copy share link"
    >
      {status === 'loading' && '⏳ Sharing…'}
      {status === 'copied' && '✓ Link copied'}
      {status === 'error' && '✕ Share failed'}
      {status === 'idle' && '🔗 Share'}
    </button>
  );
}

function CockpitToolbar({ editing, onEditToggle, onReset, scope, activeTab }) {
  // Add/remove now lives in the right-side Library panel; the toolbar only
  // exposes the cross-tab actions (reset, share, edit toggle).
  const onDefault = !!activeTab?.pinned;
  return (
    <div className="cockpit-toolbar">
      {editing && (
        <button
          type="button"
          className="cockpit-tb-btn cockpit-tb-reset"
          onClick={onReset}
          title="Reset this tab to default layout"
        >
          ↺ Reset
        </button>
      )}
      {/* Default is the canonical pinned layout — sharing it would just
          spread back the factory state, which the recipient already gets
          on first load. Hide the button to reduce noise. */}
      {!onDefault && <ShareButton scope={scope} activeTab={activeTab} />}
      <button
        type="button"
        className={`cockpit-tb-btn cockpit-tb-edit ${editing ? 'on' : ''}`}
        onClick={onEditToggle}
        title={
          editing
            ? 'Lock layout'
            : onDefault
              ? 'Default is read-only — click to create an editable copy'
              : 'Edit layout'
        }
      >
        {editing ? '🔒 Lock' : onDefault ? '✎ Customize' : '✎ Edit'}
      </button>
    </div>
  );
}

export default CockpitToolbar;
